import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Modal } from './ui';
import { useChat } from '../lib/chat';

/**
 * The "things moved" tour.
 *
 * Shown once, to explain the five things this update changed that a returning
 * player would otherwise have to discover by finding their profile different
 * from how they left it.
 *
 * Seen-state lives in localStorage keyed by user id rather than on the server.
 * That is a deliberate trade: it costs a column and a migration to make a
 * five-panel tour follow someone between devices, and the failure mode — seeing
 * it a second time on a new browser — is a shrug rather than a bug.
 */

const STORAGE_KEY = 'pg.tour.overhaul';

export const hasSeenTour = (userId: string) => {
  try {
    return window.localStorage.getItem(`${STORAGE_KEY}.${userId}`) === '1';
  } catch {
    // Private mode: treat it as seen rather than showing the tour on every
    // single page load, which would be far worse than never showing it.
    return true;
  }
};

const markSeen = (userId: string) => {
  try {
    window.localStorage.setItem(`${STORAGE_KEY}.${userId}`, '1');
  } catch {
    /* nothing to do; the tour simply reappears next session */
  }
};

interface Step {
  art: string;
  title: string;
  body: React.ReactNode;
}

const STEPS: Step[] = [
  {
    art: '★',
    title: 'My Team',
    body: (
      <>
        <p>
          Your profile no longer lists whatever happens to be standing in your Grove. Instead you
          choose <strong>six Pokémon</strong> — the ones travelling with you right now.
        </p>
        <p>
          They are what visitors see and feed, and they gain EXP whenever{' '}
          <strong>you</strong> go and help another trainer.
        </p>
        <p className="tiny muted">
          We've picked six to start you off, using what your profile used to show. Change them
          whenever you like from the Manage Team button on your profile.
        </p>
      </>
    ),
  },
  {
    art: '🌿',
    title: 'Groves, plural',
    body: (
      <>
        <p>
          You now keep <strong>up to ten Groves</strong> — habitats to sort your collection into. A
          Forest Grove, a Night Grove, somewhere for your Water types.
        </p>
        <p className="tiny muted">
          Groves no longer level up. Every one holds the same number of Pokémon, so what you earn is
          more Groves rather than a bigger one. Everything you already had is waiting in your first.
        </p>
      </>
    ),
  },
  {
    art: '♥',
    title: 'Visiting is the point',
    body: (
      <>
        <p>
          Every trainer's Groves are yours to browse. Walk through them, feed what you find, warm
          their eggs — a visitor's food is worth roughly seven times your own.
        </p>
        <p className="tiny muted">
          Your Pokémon wander around their Grove on their own now. Drag them where you want them in
          Edit mode and they'll stay in that corner.
        </p>
      </>
    ),
  },
  {
    art: '✎',
    title: 'Chat, everywhere',
    body: (
      <>
        <p>
          There's a chat dock in the bottom-right corner on every screen — Global, Help, Trade and
          Events. It stays with you as you move around the game.
        </p>
        <p className="tiny muted">
          Click anyone's name to visit their profile, their Grove or their Bazaar. You can mute the
          filter, block someone, or report a message from the same menu.
        </p>
      </>
    ),
  },
  {
    art: '🏰',
    title: 'Guild chat moved',
    body: (
      <>
        <p>
          Your guild's chat is no longer a tab on the Guild page — it's a channel in the dock,
          alongside the rest. Officers can pin announcements there, and the game posts guild news
          into it.
        </p>
        <p className="tiny muted">Everything that was said before is still there.</p>
      </>
    ),
  },
];

export function OverhaulTour({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const chat = useChat();

  const current = STEPS[step]!;
  const isLast = step === STEPS.length - 1;

  function finish() {
    markSeen(userId);
    onClose();
  }

  return (
    <Modal onClose={finish} labelledBy="tour-title">
      <div className="stack center">
        <div style={{ fontSize: '3rem', lineHeight: 1, color: 'var(--leaf)' }} aria-hidden>
          {current.art}
        </div>

        <h2 id="tour-title">{current.title}</h2>
        <div className="stack-sm" style={{ textAlign: 'left' }}>
          {current.body}
        </div>

        <div className="row" style={{ gap: 5, justifyContent: 'center' }}>
          {STEPS.map((_, index) => (
            <span
              key={index}
              className={`tour-dot ${index === step ? 'tour-dot--active' : ''}`}
              aria-hidden
            />
          ))}
        </div>

        <div className="row" style={{ width: '100%' }}>
          <Button onClick={finish} style={{ flex: 1 }}>
            Skip
          </Button>
          {step > 0 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
          {isLast ? (
            <Button
              variant="go"
              style={{ flex: 2 }}
              onClick={() => {
                // Ending on "open the chat" rather than a bare dismiss: the
                // dock is the one thing here a player might not find on their
                // own, so the tour opens it for them.
                chat.setOpen(true);
                finish();
              }}
            >
              Open chat & start
            </Button>
          ) : (
            <Button variant="go" style={{ flex: 2 }} onClick={() => setStep(step + 1)}>
              Next
            </Button>
          )}
        </div>

        <Link className="tiny muted" to="/grove" onClick={finish}>
          Take me to my Groves
        </Link>
      </div>
    </Modal>
  );
}
